import Joi from "joi";
import errorsResolver from "../schemeUtils";
import { maxLength } from "../../helpers/data-constants";
import { clientInformationProperties } from "./clientScheme";

const RISK_EXPLANATION = maxLength.SERVICE_INFORMATION_RISK;
const SPECIAL_FORM = maxLength.SPECIAL_FORM;
const CODE_SUSPICION = maxLength.CODE_SUSPICION;

const patterns = {
  generalText: `^[a-zA-Zа-яА-ЯёЁ0-9() *\\/?!%{}[\\]^<>,-.|№"_=&$#~@+'\\\\ \n\r:;«»\`]`,
  numberOnly: "^[0-9]+$",
  name: "^[a-zA-Zа-яА-ЯёЁ0-9\\- ]*$",
};

const RISK_LEVELS = {
  LOW: "Низкий",
  HIGH: "Высокий",
};

const createRiskExplanationSchema = (fieldName, isRequired = true) => {
  const baseSchema = Joi.string()
    .max(RISK_EXPLANATION)
    .pattern(new RegExp(patterns.generalText))
    .error(
      errorsResolver({
        max: `${fieldName} должно быть до ${RISK_EXPLANATION} символов`,
        pattern: `${fieldName} содержит некорректные символы`,
        required: `${fieldName} обязательно при высокой степени риска`,
      })
    );

  return isRequired ? baseSchema.required() : baseSchema.allow("");
};

const furtherActionsInfoBeanSchema = Joi.object({
  id: Joi.alternatives()
    .try(
      Joi.number(),
      Joi.allow(null)
    ),
  fullName: Joi.object({
    label: Joi.string()
      .pattern(new RegExp(patterns.name))
      .error(
        errorsResolver({
          pattern: `Поле содержит некорректные символы`,
        })
      ),
    value: Joi.string(),
  })
    .allow(null)
    .unknown(),
  decision: Joi.string()
    .max(RISK_EXPLANATION)
    .allow("")
    .optional()
    .error(
      errorsResolver({
        max: `Поле должно быть до ${RISK_EXPLANATION} символов`,
      })
    ),
  dateDecision: Joi.date()
    .iso()
    .allow('')
    .error(new Error("Некорректная дата")),
  dateCompletion: Joi.date()
    .iso()
    .allow('')
    .error(new Error("Некорректная дата")),
});

const blockTransactionsInfoBeanSchema = Joi.object({
  id: Joi.alternatives()
    .try(
      Joi.number(),
      Joi.allow(null)
    ),
  fullName: Joi.object({
    label: Joi.string()
      .pattern(new RegExp(patterns.name))
      .error(
        errorsResolver({
          pattern: `Поле содержит некорректные символы`,
        })
      ),
    value: Joi.string(),
  })
    .allow(null)
    .unknown(),
  decision: Joi.string()
    .max(RISK_EXPLANATION)
    .pattern(new RegExp(patterns.generalText))
    .allow("")
    .optional()
    .error(
      errorsResolver({
        max: `Поле должно быть до ${RISK_EXPLANATION} символов`,
        pattern: `Поле содержит некорректные символы`,
      })
    ),
  dateDecision: Joi.date()
    .iso()
    .allow('')
    .error(new Error("Некорректная дата")),
});

const suspiciousTransactionInfoBeanSchema = Joi.object({
  id: Joi.alternatives().try(Joi.number(), Joi.allow(null)),
  dealNumber: Joi.object({
    label: Joi.string(),
    value: Joi.string(),
  })
    .allow(null)
    .unknown(),
  codeSuspicion: Joi.string()
    .max(CODE_SUSPICION)
    .pattern(new RegExp(patterns.numberOnly))
    .allow("")
    .error(
      errorsResolver({
        pattern: `Поле содержит некорректные символы`,
        max: `Поле должно быть до ${CODE_SUSPICION} символов`,
      })
    ),
  referralDate: Joi.date()
    .iso()
    .allow('')
    .error(new Error("Некорректная дата")),
  specialForm: Joi.string()
    .allow("")
    .trim()
    .max(SPECIAL_FORM)
    .pattern(new RegExp(patterns.generalText))
    .error(
      errorsResolver({
        max: `Поле должно быть до ${SPECIAL_FORM} символов`,
        pattern: `Поле содержит некорректные символы`,
      })
    ),
    financeSuspiciousOperations: Joi.array().items(
    Joi.object({
      id: Joi.alternatives().try(Joi.number(), Joi.allow(null)),
      dealNumber: Joi.object({
        label: Joi.string(),
        value: Joi.alternatives().try(Joi.string(), Joi.number()),
      })
        .allow(null)
        .unknown(),
      dateTransaction: Joi.date().iso().allow('').error(new Error("Некорректная дата")),
    }))
});

const hasFilledBlockTransaction = (blockTransactions) => {
  const hasFilled = blockTransactions.some(
    (transaction) =>
      (transaction.fullName || transaction.fullName?.label) ||
      transaction.decision
  );
  return hasFilled;
};

const serviceInformationScheme = (aimAndRelationshipFeature) => {
  const schema = Joi.object({
    riskGeographic: Joi.string()
      .valid(RISK_LEVELS.LOW, RISK_LEVELS.HIGH)
      .required(),
      
    riskGeographicExplanation: Joi.string().when("riskGeographic", {
      is: RISK_LEVELS.HIGH,
      then: createRiskExplanationSchema("Обоснование степени риска"),
      otherwise: createRiskExplanationSchema("Обоснование степени риска", false),
    }),

    riskProfile: Joi.string().valid(RISK_LEVELS.LOW, RISK_LEVELS.HIGH).required(),
    
    riskProfileExplanation: Joi.string().when("riskProfile", {
      is: RISK_LEVELS.HIGH,
      then: createRiskExplanationSchema("Обоснование степени риска"),
      otherwise: createRiskExplanationSchema("Обоснование степени риска", false),
    }),

    riskOperationType: Joi.string()
      .valid(RISK_LEVELS.LOW, RISK_LEVELS.HIGH)
      .required(),
      
    riskOperationTypeExplanation: Joi.string().when("riskOperationType", {
      is: RISK_LEVELS.HIGH,
      then: createRiskExplanationSchema("Обоснование степени риска"),
      otherwise: createRiskExplanationSchema("Обоснование степени риска", false),
    }),

    riskFinal: Joi.string()
      .required()
      .error(
        errorsResolver({
          required: "Итоговая степень риска обязательна",
        })
      ),
      
    riskFinalExplanation: Joi.string().when("riskFinal", {
      is: RISK_LEVELS.HIGH,
      then: createRiskExplanationSchema("Обоснование степени риска"),
      otherwise: createRiskExplanationSchema("Обоснование степени риска", false),
    }),

    furtherActions: Joi.array().items(furtherActionsInfoBeanSchema),
    
    blockTransactions: Joi.array().items(blockTransactionsInfoBeanSchema),
    
    suspiciousTransactions: Joi.array()
      .when(Joi.ref('.'), {
        is: Joi.any().custom((_, helpers) => {
          const blockTransactions = helpers.state.ancestors[0]?.blockTransactions;
          const hasFilled = hasFilledBlockTransaction(blockTransactions);

          if (!aimAndRelationshipFeature && hasFilled) {
            return true;
          }
          return helpers.error('any.required');
        }),
      then: Joi.array().items(
        Joi.object({
          id: Joi.alternatives().try(Joi.number(), Joi.allow(null)),
          codeSuspicion: Joi.string()
            .max(CODE_SUSPICION)
            .pattern(new RegExp(patterns.numberOnly))
            .required()
            .error(
              errorsResolver({
                pattern: `Поле содержит некорректные символы`,
                max: `Поле должно быть до ${CODE_SUSPICION} символов`,
                required: "Обязательное поле",
              })
            ),
          referralDate: Joi.date()
            .iso()
            .required()
            .error(errorsResolver({
                dateFormat: `Некорректная дата`,
                required: 'Обязательное поле',
            })),
          specialForm: Joi.string()
            .trim()
            .required()
            .max(SPECIAL_FORM)
            .pattern(new RegExp(patterns.generalText))
            .error(
              errorsResolver({
                max: `Поле должно быть до ${SPECIAL_FORM} символов`,
                required: "Обязательное поле",
                pattern: `Поле содержит некорректные символы`,
              })
            ),
            financeSuspiciousOperations: Joi.array().items(
            Joi.object({
              id: Joi.alternatives().try(Joi.number(), Joi.allow(null)),
              dealNumber: Joi.object({
                label: Joi.string(),
                value: Joi.alternatives().try(Joi.string(), Joi.number()),
              })
                .allow(null)
                .unknown(),
              dateTransaction: Joi.date()
                .iso()
                .required()
                .error(errorsResolver({
                    dateFormat: `Некорректная дата`,
                    required: 'Обязательное поле',
                })),
            }))
        })
      ),
      otherwise: Joi.array().items(suspiciousTransactionInfoBeanSchema),
    }),
  });

  const additionalFields = aimAndRelationshipFeature 
    ? { lackIdentifierDataReason: clientInformationProperties.lackIdentifierDataReason }
    : {
        otherInformation: clientInformationProperties.otherInformation,
        lackIdentifierDataReason: clientInformationProperties.lackIdentifierDataReason,
      };

  return schema.keys(additionalFields);
}

export default serviceInformationScheme;
